import {
  CHECKLIST_LIBRARY,
  DEFAULT_GRANT_CHECKLIST,
  DEFAULT_SOFTWARE_CHECKLIST,
  getChecklistById,
} from "../data/checklistLibrary.js";
import { appendAccountingFinanceSection } from "../data/checklists/accountingFinance.js";

export const CUSTOM_CHECKLIST_ID = "custom";

/** Starter tasks for a blank custom checklist. */
export const BLANK_CUSTOM_TASKS = [
  { id: 1, name: "Define first step", completed: false, subtasks: [] },
  { id: 2, name: "Define second step", completed: false, subtasks: [] },
];

const DEFAULT_TASK_FIELDS = {
  model: "gpt-4",
  isRecurring: false,
  frequency: "daily",
  action: "follow_up",
  startDate: "",
  startTime: "",
  recurrenceType: "daily",
  recurrenceDays: [],
  emailTo: "",
  emailFrom: "",
  emailSubject: "",
  emailBody: "",
};

function normalizeText(value) {
  return (value || "").toLowerCase().trim();
}

function isSoftwareUpgradeType(upgradeType) {
  const t = normalizeText(upgradeType);
  return t === "tech" || t.startsWith("tech ") || t.includes("software");
}

/**
 * Expand minimal library tasks into full ToDone task objects.
 * Grant checklists optionally include the shared accounting & finance section.
 */
export function normalizeChecklistTasks(tasks, options = {}) {
  if (!tasks || !Array.isArray(tasks)) return [];

  const normalized = tasks.map((task, index) => {
    const taskId = task.id ?? index + 1;

    const subtasks = (task.subtasks || []).map((subtask, subIndex) => ({
      ...DEFAULT_TASK_FIELDS,
      ...subtask,
      id: subtask.id ?? `${taskId}-${subIndex + 1}`,
      name: subtask.name || `Step ${subIndex + 1}`,
      prompt: subtask.prompt || subtask.name || `Step ${subIndex + 1}`,
      completed: subtask.completed ?? false,
      action: subtask.action || task.action || DEFAULT_TASK_FIELDS.action,
    }));

    return {
      ...DEFAULT_TASK_FIELDS,
      ...task,
      id: taskId,
      name: task.name || `Step ${index + 1}`,
      prompt: task.prompt || task.name || `Step ${index + 1}`,
      completed: task.completed ?? false,
      subtasks,
    };
  });

  if (options.includeAccountingFinance) {
    return appendAccountingFinanceSection(normalized);
  }

  return normalized;
}

function matchesRules(entry, ctx) {
  const { match } = entry;
  if (!match) return false;

  const { upgradeName, opportunityName, grantName, upgradeType, combined } =
    ctx;

  if (match.upgradeTypesOnly && match.upgradeTypes?.length) {
    return match.upgradeTypes.some((t) =>
      normalizeText(upgradeType).includes(normalizeText(t))
    );
  }

  if (match.upgradeTypes?.length) {
    const typeHit = match.upgradeTypes.some((t) => {
      const normalized = normalizeText(t);
      if (normalized === "tech") return isSoftwareUpgradeType(upgradeType);
      return normalizeText(upgradeType).includes(normalized);
    });
    if (typeHit) return true;
  }

  for (const pattern of match.patterns || []) {
    if (
      pattern.test(upgradeName) ||
      pattern.test(opportunityName) ||
      pattern.test(grantName)
    ) {
      return true;
    }
  }

  for (const name of match.names || []) {
    const needle = normalizeText(name);
    if (needle && combined.includes(needle)) return true;
  }

  return false;
}

/**
 * Resolve which checklist template applies to an opportunity.
 */
export function resolveChecklistDefinition(opportunity, options = {}) {
  const { grantName = "", useDetailed = false } = options;

  const fields = opportunity?.fields || opportunity || {};
  const upgradeName = (fields.UpgradeName || fields.Name || "").trim();
  const opportunityName = (fields.OpportunityName || "").trim();
  const upgradeType = (fields.UpgradeType || "").trim();
  const grant = (grantName || "").trim();

  const combined = [upgradeName, opportunityName, grant]
    .map(normalizeText)
    .filter(Boolean)
    .join(" ");

  for (const entry of CHECKLIST_LIBRARY) {
    if (
      matchesRules(entry, {
        upgradeName,
        opportunityName,
        grantName: grant,
        upgradeType,
        combined,
      })
    ) {
      const tasks =
        useDetailed && entry.detailedTasks?.length
          ? entry.detailedTasks
          : entry.tasks;
      return { ...entry, tasks };
    }
  }

  if (isSoftwareUpgradeType(upgradeType)) {
    return { ...DEFAULT_SOFTWARE_CHECKLIST };
  }

  return { ...DEFAULT_GRANT_CHECKLIST };
}

/** Resolve and normalize tasks for an opportunity. */
export function resolveChecklistTasks(opportunity, options = {}) {
  const { programChecklist = null } = options;

  if (programChecklist?.tasks?.length) {
    return normalizeChecklistTasks(programChecklist.tasks, {
      includeAccountingFinance: programChecklist.checklistType === "grant",
    });
  }

  const definition = resolveChecklistDefinition(opportunity, options);
  return normalizeChecklistTasks(definition.tasks, {
    includeAccountingFinance: definition.type === "grant",
  });
}

/** Build default ToDone JSON (includes checklist metadata). */
export function buildDefaultToDoneJson(opportunity, options = {}) {
  const definition = resolveChecklistDefinition(opportunity, options);
  const tasks = normalizeChecklistTasks(definition.tasks, {
    includeAccountingFinance: definition.type === "grant",
  });

  return JSON.stringify(
    {
      checklistId: definition.id,
      checklistLabel: definition.label,
      checklistType: definition.type,
      tasks,
    },
    null,
    2
  );
}

export function getResolvedChecklistLabel(opportunity, options = {}) {
  return resolveChecklistDefinition(opportunity, options).label;
}

/** Read checklist metadata from saved ToDone JSON. */
export function parseChecklistMeta(jsonString) {
  if (!jsonString || jsonString.trim() === "") {
    return { checklistId: null, checklistLabel: null, checklistType: null };
  }

  try {
    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed)) {
      return { checklistId: null, checklistLabel: null, checklistType: null };
    }
    return {
      checklistId: parsed.checklistId || null,
      checklistLabel: parsed.checklistLabel || null,
      checklistType: parsed.checklistType || null,
    };
  } catch {
    return { checklistId: null, checklistLabel: null, checklistType: null };
  }
}

/** Build ToDone JSON for a specific library checklist id. */
export function buildChecklistJsonById(checklistId, options = {}) {
  const { useDetailed = false, label: customLabel } = options;

  let definition;
  if (checklistId === CUSTOM_CHECKLIST_ID) {
    definition = {
      id: CUSTOM_CHECKLIST_ID,
      label: customLabel || "Custom",
      type: "custom",
      tasks: BLANK_CUSTOM_TASKS,
    };
  } else {
    definition = getChecklistById(checklistId);
    if (!definition) {
      definition = { ...DEFAULT_GRANT_CHECKLIST };
    }
    if (useDetailed && definition.detailedTasks?.length) {
      definition = { ...definition, tasks: definition.detailedTasks };
    }
  }

  const tasks = normalizeChecklistTasks(definition.tasks, {
    includeAccountingFinance: definition.type === "grant",
  });

  return JSON.stringify(
    {
      checklistId: definition.id,
      checklistLabel: definition.label,
      checklistType: definition.type,
      tasks,
    },
    null,
    2
  );
}

/** Read required-info values from saved ToDone JSON. */
export function parseRequiredInfoValues(jsonString) {
  if (!jsonString || jsonString.trim() === "") return {};
  try {
    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed)) return {};
    return parsed.requiredInfoValues || {};
  } catch {
    return {};
  }
}

/** Merge task updates with existing checklist metadata for save. */
export function buildToDoneJsonWithTasks(
  tasks,
  existingJson = "",
  { requiredInfoValues, requiredInfoFields } = {}
) {
  const { defaults, existingRequiredInfo, existingRequiredInfoFields } = (() => {
    if (!existingJson) {
      return {
        defaults: null,
        existingRequiredInfo: null,
        existingRequiredInfoFields: null,
      };
    }
    try {
      const parsed = JSON.parse(existingJson);
      if (Array.isArray(parsed)) {
        return {
          defaults: null,
          existingRequiredInfo: null,
          existingRequiredInfoFields: null,
        };
      }
      return {
        defaults: parsed.defaults || null,
        existingRequiredInfo: parsed.requiredInfoValues || null,
        existingRequiredInfoFields: parsed.requiredInfoFields || null,
      };
    } catch {
      return {
        defaults: null,
        existingRequiredInfo: null,
        existingRequiredInfoFields: null,
      };
    }
  })();

  const meta = parseChecklistMeta(existingJson);

  const payload = {
    ...meta,
    tasks,
  };

  if (defaults && Object.keys(defaults).length > 0) {
    payload.defaults = defaults;
  }

  const infoValues =
    requiredInfoValues !== undefined
      ? requiredInfoValues
      : existingRequiredInfo;
  if (infoValues && Object.keys(infoValues).length > 0) {
    payload.requiredInfoValues = infoValues;
  }

  const infoFields =
    requiredInfoFields !== undefined
      ? requiredInfoFields
      : existingRequiredInfoFields;
  if (Array.isArray(infoFields) && infoFields.length > 0) {
    payload.requiredInfoFields = infoFields;
  }

  return JSON.stringify(payload, null, 2);
}
